import { RichTreeViewPro } from '@mui/x-tree-view-pro';
import { Close, Search, ArrowDropDown } from '@mui/icons-material';
import { useRef, useMemo, useState, useCallback, KeyboardEvent } from 'react';
import {
  Box,
  Chip,
  Paper,
  Popper,
  useTheme,
  TextField,
  IconButton,
  Typography,
  InputLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
  ClickAwayListener,
} from '@mui/material';

import { useTagTree } from 'hooks';
import { flattenTagTree } from 'hooks/use-tag-tree/helpers';
import { HighlightLabel } from 'components/highlight-label';

import { TagTreeSelectProps } from './types';
import { buildGroupStatusMap } from './helpers';
import { TagTreeSelectItem } from './tag-tree-select-item';

export type { TagTreeSelectProps } from './types';

export const TagTreeSelect = ({
  tags,
  value,
  label,
  error,
  onBlur,
  disabled,
  onChange,
  helperText,
  placeholder,
  groupSelection,
  size = 'medium',
  hideGroupCheckbox,
  chipProps = {
    color: 'primary',
    variant: 'filled',
  },
}: TagTreeSelectProps) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const {
    expanded,
    treeItems,
    matchCount,
    searchQuery,
    setExpanded,
    setSearchQuery,
  } = useTagTree(tags);

  const tagMap = useMemo(
    () => new Map(flattenTagTree(tags).map((t) => [t.id, t])),
    [tags]
  );

  const groupStatusMap = useMemo(
    () =>
      groupSelection ? buildGroupStatusMap(tags, new Set(value)) : undefined,
    [groupSelection, tags, value]
  );

  const handleDelete = useCallback(
    (id: string) => {
      onChange(value.filter((v) => v !== id));
    },
    [value, onChange]
  );

  const handleGroupToggle = useCallback(
    (groupId: string) => {
      const status = groupStatusMap?.get(groupId);
      if (!status) return;

      const descendantSet = new Set(status.ids);
      const newValue = status.checked
        ? value.filter((id) => !descendantSet.has(id))
        : [...new Set([...value, ...status.ids])];

      onChange(newValue);
    },
    [groupStatusMap, value, onChange]
  );

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery('');
    onBlur?.();
  };

  const handleSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const labelId = label ? `tag-tree-select-${label}` : undefined;
  const hasValue = value.length > 0;

  return (
    <FormControl fullWidth error={error} disabled={disabled}>
      {label && (
        <InputLabel id={labelId} shrink={hasValue || open || size === 'small'}>
          {label}
        </InputLabel>
      )}
      <OutlinedInput
        readOnly
        size={size}
        label={label}
        ref={anchorRef}
        onClick={handleOpen}
        notched={hasValue || open}
        sx={{ cursor: 'pointer' }}
        inputProps={{ placeholder, style: { width: hasValue ? 0 : undefined } }}
        endAdornment={
          <ArrowDropDown
            sx={{
              mr: -0.5,
              color: 'action.active',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        startAdornment={
          hasValue ? (
            <Box
              sx={{
                py: 0.5,
                gap: 0.5,
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {value.map((id) => (
                <Chip
                  key={id}
                  size="small"
                  {...chipProps}
                  label={tagMap.get(id)?.name ?? id}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDelete={disabled ? undefined : () => handleDelete(id)}
                />
              ))}
            </Box>
          ) : undefined
        }
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      <Popper
        open={open}
        placement="bottom-start"
        anchorEl={anchorRef.current}
        style={{ zIndex: theme.zIndex.modal }}
        modifiers={[
          {
            enabled: true,
            name: 'matchWidth',
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn: ({ state }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              mt: 0.5,
              maxHeight: 360,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                mb: 3,
              }}
            >
              <TextField
                autoFocus
                fullWidth
                size="small"
                value={searchQuery}
                placeholder="Search..."
                onKeyDown={handleSearchKeyDown}
                onChange={(e) => setSearchQuery(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <Search sx={{ mr: 0.5, color: 'action.active' }} />
                    ),
                    endAdornment: searchQuery ? (
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    ) : undefined,
                  },
                }}
              />
            </Box>
            {treeItems.length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  pb: 2,
                  color: 'text.secondary',
                }}
              >
                {searchQuery
                  ? `${matchCount} Result${matchCount !== 1 ? 's' : ''}`
                  : 'Name'}
              </Typography>
            )}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {treeItems.length === 0 ? (
                <Typography
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  No tags found
                </Typography>
              ) : (
                <RichTreeViewPro
                  multiSelect
                  checkboxSelection
                  items={treeItems}
                  selectedItems={value}
                  getItemLabel={(item) => item.name}
                  slots={{ item: TagTreeSelectItem }}
                  expandedItems={Array.from(expanded)}
                  onSelectedItemsChange={(_event, itemIds) => onChange(itemIds)}
                  onExpandedItemsChange={(_event, itemIds) =>
                    setExpanded(new Set(itemIds))
                  }
                  slotProps={{
                    item: {
                      hideGroupCheckbox,
                      ...(groupSelection && {
                        groupStatusMap,
                        onGroupToggle: handleGroupToggle,
                      }),
                      slots: { label: HighlightLabel },
                      slotProps: {
                        label: { query: searchQuery },
                        checkbox: { indeterminate: false },
                      },
                    } as any,
                  }}
                />
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </FormControl>
  );
};
